// @ts-nocheck
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { supportTooltipMessageKeys } from "@store/Framework/Common/Utilities/supportLinkResolver";

export type ZeroPriceActionState = "link" | "none";

export interface ZeroPriceDisplayDecision {
    actionState: ZeroPriceActionState;
    isZeroPrice: boolean;
    showDash: boolean;
    supportUrl?: string;
    tooltipMessageKey?: string;
}

export const zeroPriceMaskValue = "—";

// ─── Parameter variants (discriminated union) ────────────────────────────────

interface ProductParams {
    context: "product";
    pricing: ProductPriceDto | null | undefined;
    quoteRequired: boolean;
    isPromotionItem: boolean;
    supportUrl?: string;
}

interface LineSubtotalParams {
    context: "lineSubtotal";
    pricing: Pick<ProductPriceDto, "unitNetPrice"> | null | undefined;
    pageType: string;
}

interface SummaryParams {
    context: "summary";
    cartLines:
        | ReadonlyArray<{ pricing?: Pick<ProductPriceDto, "unitNetPrice"> | null } | null | undefined>
        | null
        | undefined;
    pageType: string;
}

interface MlcRowParams {
    context: "mlcRow";
    price: number | null | undefined;
    supportUrl?: string;
}

export type ResolveZeroPriceDisplayParams = ProductParams | LineSubtotalParams | SummaryParams | MlcRowParams;

// ─── Internal detection predicates ───────────────────────────────────────────

const isProductZeroPrice = ({ pricing, quoteRequired, isPromotionItem }: ProductParams) =>
    !quoteRequired && !isPromotionItem && pricing?.unitNetPrice === 0 && pricing?.unitRegularPrice === 0;

const isLineSubtotalZeroPrice = ({ pricing, pageType }: LineSubtotalParams) =>
    (pageType === "CartPage" || pageType === "CheckoutReviewAndSubmitPage") && pricing?.unitNetPrice === 0;

const isSummaryZeroPrice = ({ cartLines, pageType }: SummaryParams) => {
    if (pageType !== "CartPage" && pageType !== "CheckoutReviewAndSubmitPage") {
        return false;
    }
    const lines = cartLines ?? [];
    return lines.length > 0 && lines.every(line => line?.pricing?.unitNetPrice === 0);
};

const isMlcRowZeroPrice = ({ price }: MlcRowParams) => price === 0;

// ─── Decision builder ────────────────────────────────────────────────────────

const notZeroPrice: ZeroPriceDisplayDecision = {
    actionState: "none",
    isZeroPrice: false,
    showDash: false,
};

const dashOnly: ZeroPriceDisplayDecision = {
    actionState: "none",
    isZeroPrice: true,
    showDash: true,
};

const withContactAction = (supportUrl: string): ZeroPriceDisplayDecision => ({
    actionState: "link",
    isZeroPrice: true,
    showDash: true,
    supportUrl,
    tooltipMessageKey: supportTooltipMessageKeys.zeroPriceUpdatedPrice,
});

// ─── Public resolvers ─────────────────────────────────────────────────────────

export const getCurrencyAwareZeroPriceDisplay = (currency?: string | null) => {
    const normalizedCurrency = currency?.trim();

    return normalizedCurrency ? `${normalizedCurrency} ${zeroPriceMaskValue}` : zeroPriceMaskValue;
};

export const resolveZeroPriceDisplay = (params: ResolveZeroPriceDisplayParams): ZeroPriceDisplayDecision => {
    switch (params.context) {
        case "product": {
            if (!isProductZeroPrice(params)) {
                return notZeroPrice;
            }
            return params.supportUrl ? withContactAction(params.supportUrl) : dashOnly;
        }
        case "lineSubtotal": {
            return isLineSubtotalZeroPrice(params) ? dashOnly : notZeroPrice;
        }
        case "summary": {
            return isSummaryZeroPrice(params) ? dashOnly : notZeroPrice;
        }
        case "mlcRow": {
            if (!isMlcRowZeroPrice(params)) {
                return notZeroPrice;
            }
            return params.supportUrl ? withContactAction(params.supportUrl) : dashOnly;
        }
    }
};

export default resolveZeroPriceDisplay;
